import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  useCreatePersonAddress,
  useGetPersonAddressDetails,
  useGetPersonAddressLovs,
  useUpdatePersonAddress
} from '@common/slices';
import {
  CreatePersonAddressResponse,
  OrderByInput,
  PaginationInput,
  PersonAddressResponse
} from '@common/types';
import { EnhancedButtonStatus } from '@components/_old/EnhancedButton';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { getInputs } from '@modules/salesforce/persons/person-details-page/person-address-drawer/content';
import { LookupToList } from '@modules/salesforce/persons/person-details-page/person-address-drawer/utils';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { getError } from '@utils/graph-utils';

import { cloneDeep, isEmpty } from 'lodash';

interface IPersonAddressDrawerProps {
  personAddressId?: string;
  personId: string;
  open: boolean;
  isProposal?: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onSubmitSuccess?: (id: string, address: string) => void;
  filter: {
    pagination: PaginationInput;
    OrderBy: OrderByInput;
  };
}

const PersonAddressDrawer: React.FC<IPersonAddressDrawerProps> = ({
  personAddressId,
  personId,
  open,
  isProposal = false,
  onClose,
  onSuccess,
  onSubmitSuccess,
  filter
}) => {
  const [booted, setBooted] = useState<boolean>(false);
  const navigate = useNavigate();

  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({});
  const [inputsForm, setInputsForm] = useState<IDynamicFormWidgetSection[]>([]);

  const { data: detailsData, loading: detailLoading } =
    useGetPersonAddressDetails({
      variables: { personAddressId }
    });
  const { data: LovsData, loading: lovLoading } = useGetPersonAddressLovs();
  const [createPersonAddress] = useCreatePersonAddress(personId, filter);
  const [updatePersonAddress] = useUpdatePersonAddress(personId, filter);
  const getEntityInfo = async () => {
    let personAddressEntity: IPersonAddressInfo = {
      personId: '',
      personAddressId: '',
      addressType: '',
      country: '',
      countryId: '',
      zone: '',
      city: '',
      street: '',
      building: '',
      addressDetails: '',
      addressDetailsArabic: '',
      isPrimary: false
    };

    if (personAddressId) {
      // personAddressInfo
      const result = detailsData?.salesforce?.getPersonAddressDetails;
      if (result) {
        personAddressEntity = {
          personId: result.personId,
          personAddressId: result.id,
          addressType: result.addressType || '',
          country: result.address?.country || '',
          countryId: result.address?.country || '',
          zone: result.address?.zone || '',
          city: result.address?.city || '',
          street: result.address?.street || '',
          building: result.address?.building || '',
          addressDetails: result.address?.addressDetails || '',
          addressDetailsArabic: result.address?.addressDetailsArabic || '',
          isPrimary: result.isPrimary || false
        };
      }
    }

    return personAddressEntity;
  };

  const loadLovList = async () => {
    return LookupToList(LovsData);
  };

  const initialize = async () => {
    try {
      const newValues = {
        addressType: '',
        country: '',
        zone: '',
        city: '',
        street: '',
        building: '',
        addressDetails: '',
        addressDetailsArabic: '',
        isPrimary: false
      };
      const newLovs: Record<string, Record<string, string>> = {};
      const [entityData, lovData] = await Promise.all([
        getEntityInfo(),
        loadLovList()
      ]);

      if (lovData) {
        newLovs.addressType = lovData.addressType;
        newLovs.country = lovData.countries;
        newLovs.zone = lovData.zone;
      }

      if (entityData) {
        Object.assign(newValues, {
          addressType: entityData.addressType,
          country: entityData.countryId,
          zone: entityData.zone,
          city: entityData.city,
          street: entityData.street,
          building: entityData.building,
          addressDetails: entityData.addressDetails,
          addressDetailsArabic: entityData.addressDetailsArabic,
          isPrimary: entityData.isPrimary
        });
      }

      setLovs(newLovs);
      setInputsForm(getInputs(newValues, newLovs, isProposal));
      setBooted(true);
    } catch {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  function isCreatePersonAddressResponse(
    response: PersonAddressResponse
  ): response is CreatePersonAddressResponse {
    return 'createPersonAddress' in response.salesforce;
  }
  const submitForm = async (vs: Record<string, any>) => {
    const [data] = normalizeDynamicFormDrawerValues(inputsForm, vs);
    setFormDisabled(true);
    setSubmitButtonState('loading');

    try {
      const input = {
        personId,
        type: data.addressType,
        country: data.country,
        city: data.city,
        isPrimary: isProposal ? true : data.isPrimary,
        zone: data.zone,
        street: data.street,
        building: data.building,
        addressDetails: data.addressDetails,
        addressDetailsArabic: data.addressDetailsArabic
      };

      const modifyInput = {
        id: personAddressId,
        personId,
        type: data.addressType,
        country: data.country,
        city: data.city,
        isPrimary: !!data.isPrimary,
        zone: data.zone,
        street: data.street,
        building: data.building,
        addressDetails: data.addressDetails,
        addressDetailsArabic: data.addressDetailsArabic
      };

      let res;
      if (personAddressId) {
        res = await updatePersonAddress({
          input: modifyInput
        });
      } else {
        res = await createPersonAddress({
          input
        });
      }

      if (isEmpty(res.errors)) {
        toast.success(
          <ToastSuccessMessage>
            {personAddressId
              ? 'Person Address was modified successfully!'
              : 'Person Address was added successfully!'}
          </ToastSuccessMessage>
        );

        const newAddressId = isCreatePersonAddressResponse(res.data)
          ? res?.data?.salesforce.createPersonAddress
          : '';
        const fullAddress = [
          input.building,
          input.street,
          input.zone,
          input.city,
          input.country
        ]
          .filter(Boolean)
          .join(', ');

        setTimeout(() => {
          setSubmitButtonState('success');
          setFormDisabled(false);
          if (onSubmitSuccess) onSubmitSuccess(newAddressId, fullAddress);
          if (onSuccess) onSuccess();
          onClose();
          if (!personId) {
            navigate(`/salesforce/persons/${personId}`);
          }
        }, 500);
      } else {
        setFormDisabled(false);
        setSubmitButtonState(undefined);
        toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
      }
    } catch {
      setFormDisabled(false);
      setSubmitButtonState(undefined);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  useEffect(() => {
    if (!detailLoading && !lovLoading) initialize();
  }, [detailLoading, lovLoading]);

  const onChange = async (
    fieldName: string,
    value: any,
    sectionIndex: number,
    newValues: Record<string, any>
  ) => {
    const newLovs = cloneDeep(lovs);

    if (fieldName === 'country') {
      if (value === 'LB') {
        // newValues.zone = lovs.zone;
        // setLovs(newLovs);
      } else {
        newValues.zone = null;
      }
    }

    setInputsForm(getInputs(newValues, newLovs, isProposal));
  };

  return (
    <DynamicFormDrawer
      title={
        personAddressId
          ? 'Edit Address'
          : isProposal
            ? 'Add Person Primary Address'
            : 'New Person Address'
      }
      onClose={onClose}
      isOpen={open}
      loading={!booted}
      sections={inputsForm}
      onSubmit={submitForm}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
      onChange={onChange}
    />
  );
};

export { PersonAddressDrawer };
